import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import FileUpload from '../components/FileUpload';
import DownloadSection from '../components/DownloadSection';
import { Card } from 'primereact/card';
import { uploadImages, authAPI } from '../services/api';
import axios from 'axios';

// Determine API base URL based on environment
const API_BASE_URL = window.location.hostname === 'localhost' 
    ? 'http://localhost:8000/api'
    : '/api';

const Home = () => {
    const [conversionProgress, setConversionProgress] = useState([]);
    const [downloadLinks, setDownloadLinks] = useState([]);
    const [zipDownloadLink, setZipDownloadLink] = useState(null);
    const [isConverting, setIsConverting] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [resizeOption, setResizeOption] = useState('original');
    const [quality, setQuality] = useState(80);
    const [isTokenValid, setIsTokenValid] = useState(null); // null = loading, true = valid, false = invalid
    const [isValidating, setIsValidating] = useState(true);
    const toastRef = React.useRef(null);

    // Validate token on component mount
    useEffect(() => {
        const validateToken = async () => {
            setIsValidating(true);
            try {
                const token = authAPI.getToken();
                
                if (!token) {
                    console.log('No token found in URL');
                    setIsTokenValid(false);
                    return;
                }

                // Send a test request to the backend to validate the token
                const headers = { 'x-token': token };
                const response = await axios.get(
                    window.location.hostname === 'localhost' 
                        ? 'http://localhost:8000/api/validate-token' 
                        : '/api/validate-token',
                    { headers }
                );

                if (response.status === 200) {
                    setIsTokenValid(true);
                    console.log('Token validation successful');
                } else {
                    setIsTokenValid(false);
                    console.log('Token validation failed');
                }
            } catch (error) {
                console.error('Token validation error:', error);
                setIsTokenValid(false);
            } finally {
                setIsValidating(false);
            }
        };

        validateToken();
    }, []);

    const handleFilesUploaded = (files) => {
        if (isConverting) {
            toastRef.current?.show({ 
                severity: 'warn', 
                summary: 'Conversion in Progress', 
                detail: 'Please wait for the current conversion to complete.',
                life: 3000 
            });
            return;
        }

        console.log('Files uploaded:', files);
        
        // Accumulate files - add new files to existing ones
        // Filter out duplicates by filename
        const existingFileNames = uploadedFiles.map(f => f.name);
        const newUniqueFiles = files.filter(f => !existingFileNames.includes(f.name));
        const accumulatedFiles = [...uploadedFiles, ...newUniqueFiles];
        
        setUploadedFiles(accumulatedFiles);
        setConversionProgress([]);
        setIsCompleted(false);
        setDownloadLinks([]);
        setZipDownloadLink(null);
        
        toastRef.current?.show({ 
            severity: 'success', 
            summary: 'Files Selected', 
            detail: `${accumulatedFiles.length} file(s) selected in total. Click "Convert Images" to start conversion.`,
            life: 3000 
        });
    };

    const handleResizeChange = (option) => {
        setResizeOption(option);
        console.log('Resize option changed:', option);
    };

    const handleQualityChange = (quality) => {
        setQuality(quality);
        console.log('Quality changed:', quality);
    };

    const handleConvert = async () => {
        if (uploadedFiles.length === 0) {
            toastRef.current?.show({ 
                severity: 'info', 
                summary: 'No Files', 
                detail: 'Please upload HEIC images first.',
                life: 3000 
            });
            return;
        }

        // Check if token is available
        const token = authAPI.getToken();
        if (!token) {
            toastRef.current?.show({ 
                severity: 'error', 
                summary: 'No Token', 
                detail: 'Authentication token is required. Pass token in URL: ?token=YOUR_TOKEN',
                life: 5000 
            });
            return;
        }

        // Initialize progress for each file
        const progress = uploadedFiles.map((file) => ({
            filename: file.name,
            percentage: 0
        }));
        setConversionProgress(progress);

        // Start conversion
        setIsConverting(true);
        console.log('Convert clicked with resize option:', resizeOption, 'quality:', quality);
        
        try {
            // Create FormData to send files and options
            const formData = new FormData();
            uploadedFiles.forEach((file) => {
                formData.append('files', file);
            });
            formData.append('quality', quality);
            formData.append('resize_option', resizeOption);

            // Call backend API using new uploadImages function with token support
            const response = await uploadImages(formData, (progressEvent) => {
                // Update progress for all files uniformly
                const percentComplete = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                const updatedProgress = progress.map(item => ({
                    ...item,
                    percentage: Math.min(percentComplete, 99) // Cap at 99% until fully complete
                }));
                setConversionProgress(updatedProgress);
            });

            // Handle successful response
            setIsConverting(false);
            setIsCompleted(true);
            
            // Set download links from response
            const convertedFiles = response.converted_files || [];
            const zipDownloadUrl = response.zip_download_url || '';
            
            setDownloadLinks(convertedFiles);
            setZipDownloadLink(zipDownloadUrl);

            // Update progress to 100%
            setConversionProgress(progress.map(item => ({
                ...item,
                percentage: 100
            })));

            // Clear uploaded files after successful conversion
            setUploadedFiles([]);

            toastRef.current?.show({ 
                severity: 'success', 
                summary: 'Conversion Complete', 
                detail: 'All images have been converted successfully.',
                life: 3000 
            });
        } catch (error) {
            console.error('Conversion error:', error);
            setIsConverting(false);
            
            // Extract error message
            let errorMessage = 'Failed to convert images. Please try again.';
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            toastRef.current?.show({ 
                severity: 'error', 
                summary: 'Conversion Failed', 
                detail: errorMessage,
                life: 5000 
            });
        }
    };

    const handleCancel = () => {
        setConversionProgress([]);
        setDownloadLinks([]);
        setZipDownloadLink(null);
        setUploadedFiles([]);
        setIsConverting(false);
        setIsCompleted(false);
        toastRef.current?.show({ 
            severity: 'info', 
            summary: 'Cancelled', 
            detail: 'Conversion cancelled and files cleared.',
            life: 3000 
        });
    };

    return (
        <div className="min-h-screen bg-surface-50 py-6">
            <Toast ref={toastRef} position="top-right" />
            
            {/* Show loading state while validating token */}
            {isValidating && (
                <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center">
                        <div className="inline-block">
                            <i className="pi pi-spin pi-spinner text-4xl text-primary" style={{ fontSize: '3rem' }}></i>
                        </div>
                        <p className="text-xl text-gray-600 mt-4">Validating access...</p>
                    </div>
                </div>
            )}

            {/* Show error message if token is invalid */}
            {!isValidating && !isTokenValid && (
                <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center">
                        <div className="mb-4">
                            <i className="pi pi-exclamation-triangle text-5xl text-red-500" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h1 className="text-4xl font-bold text-gray-800 mb-2">Service not available</h1>
                        <p className="text-lg text-gray-600 mb-6">Invalid or missing authentication token</p>
                        <p className="text-sm text-gray-500">Please provide a valid token in the URL: ?token=YOUR_TOKEN</p>
                    </div>
                </div>
            )}

            {/* Show main content if token is valid */}
            {!isValidating && isTokenValid && (
                <div className="p-0">
                    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                        {/* Header Section */}
                        <div className="mb-1 mt-1 text-center">
                            <h1 className="text-4xl font-bold mb-5 text-primary">
                                HEIC to JPG Converter
                            </h1>
                        </div>

                        {/* File Upload and Options Section */}
                        <div className="mb-6">
                          
                               <Card>
                                <div className="mb-4">
                                    <FileUpload 
                                        onFilesUploaded={handleFilesUploaded}
                                        onResizeChange={handleResizeChange}
                                        onQualityChange={handleQualityChange}
                                        disabled={isConverting}
                                        conversionProgress={conversionProgress}
                                    />
                                </div>

                                <Button 
                                label={isConverting ? "Converting..." : uploadedFiles.length > 0 ? `Convert ${uploadedFiles.length} Image(s)` : "Convert Images"}
                                icon="pi pi-check"
                                onClick={handleConvert}
                                
                                disabled={isConverting || uploadedFiles.length === 0}
                                className="p-button"
                                loading={isConverting}
                            />

                            {/* if isCompleted is true, show download button here to download ZIP file*/}
                            {isCompleted && (
                                <Button 
                                    label="Download All as ZIP" 
                                    icon="pi pi-download"
                                    onClick={() => {
                                        if (zipDownloadLink) {
                                            window.open(zipDownloadLink, '_blank');
                                        }
                                    }}
                                    disabled={!zipDownloadLink}
                                    className="p-button ml-3 p-button-success"
                                />
                            )}
                        </Card>
                        </div>

                        {/* Convert Button */}
                        <div className="mb-6 text-center">
                              
                        </div>

                    

                        {/* Download Section - Shown after completion */}
                        {isCompleted && (
                            <div className="mb-6">
                                <div className="p-3 bg-green-50 border border-green-200 border-round mb-4">
                                    <p className="text-green-700 font-semibold">✓ Conversion Completed!</p>
                                </div>
                                <DownloadSection 
                                    downloadLinks={downloadLinks}
                                    zipDownloadLink={zipDownloadLink}
                                />
                            </div>
                        )}

                        {/* Cancel Button */}
                        {(isConverting || isCompleted || conversionProgress.length > 0) && (
                            <div className="text-center mt-6">
                                <Button 
                                    label="Cancel" 
                                    icon="pi pi-times"
                                    onClick={handleCancel}
                                    className="p-button-danger p-button-lg"
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Home;